import { useEffect, useMemo, useState } from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { IoChevronDown } from 'react-icons/io5';
import { TbCurrencyDollar, TbCurrencyDollarOff } from 'react-icons/tb';
import RequestIcon from 'src/screens/request/RequestIcon';
import { parseFormBuilder } from 'src/screens/request/RequestViewPage';
import {
  convertMinToHrsMin,
  getStatusColor,
  getStatusColorHex,
  getRequestStatusLabel,
  parseDateToMMMDD,
} from 'src/utils/stringHelpers';
import './index.style.scss';

const { Text } = Typography;

interface Timelog {
  uuid: string;
  comment: string;
  date: string;
  minutes: number;
  user_name: string;
  source: string;
  source_title: string;
  billable: boolean;
}

const BILLABLE_ICON_SIZE = 16;

const BillableIndicator = ({ billable }: { billable: boolean }) => (
    <Tooltip title={billable ? 'Billable' : 'Non-billable'}>
    <span className="inline-flex leading-none">
      {billable ? (
          <TbCurrencyDollar size={BILLABLE_ICON_SIZE} color="#35a220" />
      ) : (
          <TbCurrencyDollarOff size={BILLABLE_ICON_SIZE} color="red" />
      )}
    </span>
    </Tooltip>
);

interface RequestItemCollapseProps {
  request: Record<string, any>;
  timelogs: Timelog[];
  isDetailed?: boolean;
}

const TIMELOGS_PER_PAGE = 20;

const RequestItemCollapse = ({ request, timelogs, isDetailed = true }: RequestItemCollapseProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(TIMELOGS_PER_PAGE);

  const statusColor = getStatusColor(request.status);
  const borderColor = getStatusColorHex(request.status);

  const formBuilder = useMemo(() => parseFormBuilder(request), [request]);

  const requestIconId = useMemo(() => {
    if (typeof request.description === 'object' && request.description?.id) {
      return request.description.id;
    }
    return formBuilder.id;
  }, [request.description, formBuilder.id]);

  const billableMinutes = useMemo(
      () =>
          timelogs.reduce(
              (total, timelog) => (timelog.billable ? total + timelog.minutes : total),
              0
          ),
      [timelogs]
  );

  const sortedTimelogs = useMemo(
      () => [...timelogs].sort((a, b) => dayjs(b.date).diff(dayjs(a.date))),
      [timelogs]
  );

  const displayedTimelogs = sortedTimelogs.slice(0, displayedCount);
  const hasMore = displayedCount < sortedTimelogs.length;

  const compactGroupedTimelogs = useMemo(() => {
    const grouped: Record<string, { minutes: number; count: number }> = {};
    timelogs.forEach((timelog) => {
      const key = `${timelog.user_name}|${timelog.source_title}|${timelog.billable}`;
      if (!grouped[key]) {
        grouped[key] = { minutes: 0, count: 0 };
      }
      grouped[key].minutes += timelog.minutes;
      grouped[key].count += 1;
    });

    return Object.entries(grouped).map(([key, value]) => {
      const [user_name, source_title, billable] = key.split('|');
      return {
        user_name,
        source_title,
        billable: billable === 'true',
        minutes: value.minutes,
        count: value.count,
      };
    });
  }, [timelogs]);

  useEffect(() => {
    if (!isOpen) {
      setDisplayedCount(TIMELOGS_PER_PAGE);
    }
  }, [isOpen]);

  return (
      <div
          className="request-item-collapse rounded-lg bg-white"
          style={{
            borderLeft: `3px solid ${borderColor}`,
          }}
      >
        <div
            className="request-item-collapse__header cursor-pointer p-3 hover:bg-gray-50 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
        >
          <div className="relative box-content flex max-w-full flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex min-w-[100px] flex-1 items-center gap-x-3 leading-none overflow-hidden">
              <div className="flex-none rounded bg-blue-50 p-2 text-2xl leading-none">
                <RequestIcon formId={requestIconId} />
              </div>

              <div className="flex-auto overflow-hidden min-w-0">
                <Text className="!block !text-xs" type="secondary" ellipsis>
                  {request.title}
                </Text>

                <Text className="font-bold" ellipsis>
                  {request.subject}
                </Text>
              </div>
            </div>

            <div className="flex w-full items-center justify-between gap-x-3 max-md:flex-wrap min-[1400px]:w-auto min-[1400px]:justify-between">
              <div className="min-w-[180px] flex-none overflow-hidden leading-none max-sm:hidden sm:w-auto sm:flex-auto md:w-auto md:flex-none text-left">
                <div className="max-sm:hidden">
                  <Text className="!text-xs" type="secondary" ellipsis>
                    {request.creator?.name ? `by ${request.creator.name}` : 'No user'}
                  </Text>
                </div>

                <div className="text-sm">
                  {request.target_date
                      ? `Delivery: ${parseDateToMMMDD(request.target_date, true)}`
                      : 'No due date'}
                </div>
              </div>

              <div className="w-[140px] flex-none max-sm:hidden sm:flex-none ml-8">
                <div className="text-xs text-gray-400">Time Spent</div>
                <div className="text-sm">
                  {request.spent_in_period ? `${convertMinToHrsMin(request.spent_in_period)}h` : 'No time spent'}
                </div>
              </div>

              <div className="w-[140px] flex-none max-sm:hidden sm:flex-none">
                <div className="text-xs text-gray-400">Billable</div>
                <div className="text-sm">
                  {billableMinutes ? `${convertMinToHrsMin(billableMinutes)}h` : 'No billable time'}
                </div>
              </div>

              <div className="min-w-[140px] right-3 top-3 flex w-auto flex-none items-center justify-end gap-2 max-sm:absolute">
                <Tag color={statusColor} style={{ margin: 0 }}>
                  {getRequestStatusLabel(request.status)}
                </Tag>

                <div
                    className="request-item-collapse__arrow transition-transform text-lg leading-none flex items-center justify-center"
                    style={{
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                >
                  <IoChevronDown />
                </div>
              </div>
            </div>
          </div>
        </div>

        {isOpen && timelogs.length > 0 && (
            <div className="request-item-collapse__content p-3">
              {isDetailed ? (
                  <table className="text-xs text-black">
                    <thead>
                    <tr style={{ backgroundColor: '#F0F0F0' }}>
                      <th className="text-left py-2 px-2 font-semibold w-24 text-black">Date</th>
                      <th className="text-left py-2 px-2 font-semibold w-20 text-black">Tracked</th>
                      <th className="text-left py-2 px-2 font-semibold w-32 text-black">User</th>
                      <th className="text-left py-2 px-2 font-semibold flex-1 text-black request-item-collapse__description-header">Description</th>
                      <th className="text-center py-2 px-2 font-semibold w-12 text-black">Billable</th>
                    </tr>
                    </thead>
                    <tbody>
                    {displayedTimelogs.map((timelog) => (
                        <tr key={timelog.uuid} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-2 whitespace-nowrap text-black">
                            {dayjs(timelog.date).format('MM/DD/YYYY')}
                          </td>
                          <td className="py-2 px-2 whitespace-nowrap text-black">
                            {convertMinToHrsMin(timelog.minutes)}h
                          </td>
                          <td className="py-2 px-2 whitespace-nowrap text-blue-600">{timelog.user_name}</td>
                          <td className="py-2 px-2 text-black request-item-collapse__description">{timelog.source_title || '-'}</td>
                          <td className="py-2 px-2">
                            <div className="flex justify-center">
                              <BillableIndicator billable={timelog.billable} />
                            </div>
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
              ) : (
                  <table className="text-xs text-black">
                    <thead>
                    <tr style={{ backgroundColor: '#F0F0F0' }}>
                      <th className="text-left py-2 px-2 font-semibold w-20 text-black">Tracked</th>
                      <th className="text-left py-2 px-2 font-semibold w-32 text-black">User</th>
                      <th className="text-left py-2 px-2 font-semibold flex-1 text-black request-item-collapse__description-header">Description</th>
                      <th className="text-center py-2 px-2 font-semibold w-12 text-black">Billable</th>
                    </tr>
                    </thead>
                    <tbody>
                    {compactGroupedTimelogs.map((item, idx) => (
                        <tr key={`${item.user_name}-${item.source_title}-${item.billable}-${idx}`} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-2 whitespace-nowrap text-black">
                            {convertMinToHrsMin(item.minutes)}h
                          </td>
                          <td className="py-2 px-2 whitespace-nowrap text-blue-600">{item.user_name}</td>
                          <td className="py-2 px-2 text-black request-item-collapse__description">{item.source_title || '-'}</td>
                          <td className="py-2 px-2">
                            <div className="flex justify-center">
                              <BillableIndicator billable={item.billable} />
                            </div>
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
              )}

              {isDetailed && hasMore && (
                  <button
                      onClick={() => setDisplayedCount(displayedCount + TIMELOGS_PER_PAGE)}
                      className="w-full mt-3 py-2 px-2 text-sm font-medium text-center bg-blue-50 hover:bg-blue-100 text-blue-600 rounded transition-colors"
                  >
                    Load more
                  </button>
              )}
            </div>
        )}

        {isOpen && timelogs.length === 0 && (
            <div className="p-3 text-center text-gray-500 text-xs">
              No timelogs available
            </div>
        )}
      </div>
  );
};

export default RequestItemCollapse;
